// @flow

// memo:
// 植生遷移のグラフ
// https://docs.google.com/spreadsheets/d/1WafL769itE3Fxs19T6K67VyJkcBxGPXxRND-vH_SrK4/edit#gid=0
// - type が s_tree で始まる木は陽樹
// - type が d_tree で始まる木は陰樹

// 種の出現率はランダム（現状、風で飛んでくる想定なので）
const spawnRate = 0.005;

const maxDHeight = 6;
const maxSHeight = 4;
const defaultPrice = 100;

const defaultDie = {
  conditions: [
    ['unit', 'age', ['hill', 7000, 1, 0, 2], '寿命'],
    ['cell', 'soilNutrition', ['hill', 1, 1, 0, 2, true], '栄養不足'],
    ['cell', 'water', ['>=', 95], '水没'],
  ],
};

const defaultSDie = {
  conditions: [
    ['unit', 'age', ['hill', 5000, 1, 0, 2], '寿命'],
    ['cell', 'soilNutrition', ['hill', 1, 1, 0, 2, true], '栄養不足'],
    ['unit', 'treeSunlight', ['hill', 0.2, 1, 0, 2, true], '日光不足'],
  ],
};

const weedDie = {
  conditions: [['unit', 'age', ['hill', 100, 1, 0, 2], '寿命']],
};
const seedDie = {
  conditions: [
    ['unit', 'age', ['hill', 100, 1, 0, 2], '寿命'],
    ['cell', 'soilNutrition', ['hill', 5, 1, 0, 2, true], '栄養不足'],
  ],
};
const sproutDie = defaultDie;
const sproutSDie = {
  conditions: [
    ['unit', 'age', ['hill', 5000, 1, 0, 2], '寿命'],
    ['cell', 'soilNutrition', ['hill', 1, 1, 0, 2, true], '栄養不足'],
    ['unit', 'treeSunlight', ['hill', 0.39, 1, 0, 2, true], '日光不足'],
  ],
};
const childDie = defaultDie;
const childSDie = {
  conditions: [
    ['unit', 'age', ['hill', 5000, 1, 0, 2], '寿命'],
    ['cell', 'soilNutrition', ['hill', 1, 1, 0, 2, true], '栄養不足'],
    ['unit', 'treeSunlight', ['hill', 0.39, 1, 0, 2, true], '日光不足'],
  ],
};
const youngDie = defaultDie;
const youngSDie = defaultSDie;
const adultDie = defaultDie;
const adultSDie = defaultSDie;

const seedEffects = [];
const sproutEffects = [
  { type: 'water', value: -2, range: 1 },
  { type: 'soilNutrition', value: -2, range: 1 },
];
const childEffects = sproutEffects;
const youngEffects = [
  { type: 'water', value: -3, range: 1 },
  { type: 'soilNutrition', value: -3, range: 1 },
];
const adultEffects = [
  { type: 'water', value: -5, range: 1 },
  { type: 'soilNutrition', value: -10, range: 2 },
];

const evolveToSSprout = treeName => {
  return {
    unit: `s_${treeName}_sprout`,
    conditions: [['cell', 'soilNutrition', ['hill', 100, 1, 0, 2]]],
  };
};

const evolveToSChild = treeName => {
  return {
    unit: `s_${treeName}_child`,
    conditions: [['unit', 'treeHeight', ['>=', 1.3]]],
  };
};

const evolveToSYoung = treeName => {
  return {
    unit: `s_${treeName}_young`,
    conditions: [['unit', 'treeHeight', ['>=', 2.3]]],
  };
};

const evolveToSAdult = treeName => {
  return {
    unit: `s_${treeName}_adult`,
    conditions: [['unit', 'treeHeight', ['>=', 3.5]]],
  };
};

const evolveToDSprout = treeName => {
  return {
    unit: `d_${treeName}_sprout`,
    conditions: [['cell', 'soilNutrition', ['hill', 250, 1, 0, 2]]],
  };
};

const evolveToDChild = treeName => {
  return {
    unit: `d_${treeName}_child`,
    conditions: [['unit', 'treeHeight', ['>=', 1.3]]],
  };
};

const evolveToDYoung = treeName => {
  return {
    unit: `d_${treeName}_young`,
    conditions: [['unit', 'treeHeight', ['>=', 2.3]]],
  };
};

const evolveToDAdult = treeName => {
  return {
    unit: `d_${treeName}_adult`,
    conditions: [['unit', 'treeHeight', ['>=', 3]]],
  };
};

const templates: UnitTemplate[] = [
  {
    type: 'weed1s',
    name: '雑草',
    description: '',
    categories: ['weed'],
    skipNotify: true,
    effects: [
      {
        type: 'water',
        value: -1,
        range: 1,
      },
      {
        type: 'soilNutrition',
        value: -1,
        range: 1,
      },
    ],
    spawn: {
      rate: 0.05,
      conditions: [],
    },
    die: weedDie,
    monthlyCarbonFixAmount: 0,
  },
  {
    type: 'd_tree1_seed',
    name: 'ブナの種',
    description: '',
    categories: ['tree-seed'],
    effects: seedEffects,
    spawn: {
      rate: spawnRate,
      conditions: [],
    },
    die: seedDie,
    evolve: evolveToDSprout('tree1'),
    price: defaultPrice,
    monthlyCarbonFixAmount: 0,
  },
  {
    type: 'd_tree1_sprout',
    name: 'ブナの芽',
    description: '',
    categories: ['tree'],
    effects: sproutEffects,
    die: sproutDie,
    evolve: evolveToDChild('tree1'),
    maxHeight: maxDHeight,
    monthlyCarbonFixAmount: 0.272727 * 67,
  },
  {
    type: 'd_tree1_child',
    name: 'ブナの稚樹',
    description: '',
    categories: ['tree'],
    effects: childEffects,
    die: childDie,
    evolve: evolveToDYoung('tree1'),
    maxHeight: maxDHeight,
    monthlyCarbonFixAmount: 0.272727 * 1083,
  },
  {
    type: 'd_tree1_young',
    name: 'ブナの若木',
    description: '',
    categories: ['tree'],
    season: ['autumn', 'winter'],
    effects: youngEffects,
    die: youngDie,
    evolve: evolveToDAdult('tree1'),
    maxHeight: maxDHeight,
    monthlyCarbonFixAmount: 0.272727 * 1242,
  },
  {
    type: 'd_tree1_adult',
    name: 'ブナの成木',
    description:
      'ブナ（橅）日本の冷温帯林の代表的な極相樹種。世界遺産の白神山地ブナ林が有名。どんぐりはクマなど動植物の主要な資源となるが、生産の年変動が激しいため凶作時にはクマが人里に現れる原因となる。陰樹。',
    categories: ['tree'],
    season: ['autumn', 'winter'],
    effects: adultEffects,
    die: adultDie,
    maxHeight: maxDHeight,
    monthlyCarbonFixAmount: 0.272727 * 608,
  },
  {
    type: 'd_tree2_seed',
    name: 'スギの種',
    description: '',
    categories: ['tree-seed'],
    effects: seedEffects,
    spawn: {
      rate: spawnRate,
      conditions: [],
    },
    die: seedDie,
    evolve: evolveToDSprout('tree2'),
    price: defaultPrice,
    monthlyCarbonFixAmount: 0,
  },
  {
    type: 'd_tree2_sprout',
    name: 'スギの芽',
    description: '',
    categories: ['tree'],
    effects: sproutEffects,
    die: sproutDie,
    evolve: evolveToDChild('tree2'),
    maxHeight: maxDHeight,
    monthlyCarbonFixAmount: 0.272727 * 25,
  },
  {
    type: 'd_tree2_child',
    name: 'スギの稚樹',
    description: '',
    categories: ['tree'],
    effects: childEffects,
    die: childDie,
    evolve: evolveToDYoung('tree2'),
    maxHeight: maxDHeight,
    monthlyCarbonFixAmount: 0.272727 * 1392,
  },
  {
    type: 'd_tree2_young',
    name: 'スギの若木',
    description: '',
    categories: ['tree'],
    effects: youngEffects,
    die: youngDie,
    evolve: evolveToDAdult('tree2'),
    maxHeight: maxDHeight,
    monthlyCarbonFixAmount: 0.272727 * 2767,
  },
  {
    type: 'd_tree2_adult',
    name: 'スギの成木',
    description:
      'スギ（杉）名前の語源は直線的に真っ直ぐ（すぐ）成長する事による。有用材として日本全国に植えられ、花粉症の原因となる。特に寿命が長い樹木であり、屋久島には樹齢2,000年を超えるものもある。陰樹。',
    categories: ['tree'],
    season: ['spring'],
    effects: adultEffects,
    die: adultDie,
    maxHeight: maxDHeight,
    monthlyCarbonFixAmount: 0.272727 * 925,
  },
  {
    type: 'd_tree3_seed',
    name: 'イタヤカエデの種',
    description: '',
    categories: ['tree-seed'],
    effects: seedEffects,
    die: seedDie,
    evolve: evolveToDSprout('tree3'),
    price: 500,
    monthlyCarbonFixAmount: 0,
  },
  {
    type: 'd_tree3_sprout',
    name: 'イタヤカエデの芽',
    description: '',
    categories: ['tree'],
    effects: sproutEffects,
    die: sproutDie,
    evolve: evolveToDChild('tree3'),
    maxHeight: maxDHeight,
    monthlyCarbonFixAmount: 0.272727 * 51,
  },
  {
    type: 'd_tree3_child',
    name: 'イタヤカエデの稚樹',
    description: '',
    categories: ['tree'],
    effects: childEffects,
    die: childDie,
    evolve: evolveToDYoung('tree3'),
    maxHeight: maxDHeight,
    monthlyCarbonFixAmount: 0.272727 * 826,
  },
  {
    type: 'd_tree3_young',
    name: 'イタヤカエデの若木',
    description: '',
    categories: ['tree'],
    season: ['autumn', 'winter'],
    effects: youngEffects,
    die: youngDie,
    evolve: evolveToDAdult('tree3'),
    maxHeight: maxDHeight,
    monthlyCarbonFixAmount: 0.272727 * 997,
  },
  {
    type: 'd_tree3_adult',
    name: 'イタヤカエデの成木',
    description:
      'イタヤカエデ（板屋楓）カエデ属の一種であり、特徴的な葉の形をしている。秋には黄葉する。メープルシロップの材料であるサトウカエデには品質に劣るが樹液から砂糖が精製できる。陰樹。',
    categories: ['tree'],
    season: ['spring', 'summer', 'winter'],
    effects: adultEffects,
    die: adultDie,
    maxHeight: maxDHeight,
    monthlyCarbonFixAmount: 0.272727 * 488,
  },
  {
    type: 's_tree1_seed',
    name: 'ケヤキの種',
    description: '',
    categories: ['tree-seed'],
    effects: seedEffects,
    spawn: {
      rate: spawnRate,
      conditions: [],
    },
    die: seedDie,
    evolve: evolveToSSprout('tree1'),
    price: defaultPrice,
    monthlyCarbonFixAmount: 0,
  },
  {
    type: 's_tree1_sprout',
    name: 'ケヤキの芽',
    description: '',
    categories: ['tree'],
    effects: sproutEffects,
    die: sproutSDie,
    evolve: evolveToSChild('tree1'),
    maxHeight: maxSHeight,
    monthlyCarbonFixAmount: 0.272727 * 71,
  },
  {
    type: 's_tree1_child',
    name: 'ケヤキの稚樹',
    description: '',
    categories: ['tree'],
    effects: childEffects,
    die: childSDie,
    evolve: evolveToSYoung('tree1'),
    maxHeight: maxSHeight,
    monthlyCarbonFixAmount: 0.272727 * 1155,
  },
  {
    type: 's_tree1_young',
    name: 'ケヤキの若木',
    description: '',
    categories: ['tree'],
    effects: youngEffects,
    die: youngSDie,
    season: ['autumn', 'winter'],
    evolve: evolveToSAdult('tree1'),
    maxHeight: maxSHeight,
    monthlyCarbonFixAmount: 0.272727 * 1284,
  },
  {
    type: 's_tree1_adult',
    name: 'ケヤキの成木',
    description:
      'ケヤキ（欅）扇を開いたような樹形や紅葉の美しさから、街路樹としてよく利用されるとともに、多くの市町村のシンボルに大欅や並木が指定される。陽樹。',
    categories: ['tree'],
    season: ['autumn', 'winter'],
    effects: adultEffects,
    die: adultSDie,
    maxHeight: maxSHeight,
    monthlyCarbonFixAmount: 0.272727 * 629,
  },
  {
    type: 's_tree2_seed',
    name: 'クリの種',
    description: '',
    categories: ['tree-seed'],
    effects: seedEffects,
    spawn: {
      rate: spawnRate,
      conditions: [],
    },
    die: seedDie,
    evolve: evolveToSSprout('tree2'),
    price: defaultPrice,
    monthlyCarbonFixAmount: 0,
  },
  {
    type: 's_tree2_sprout',
    name: 'クリの芽',
    description: '',
    categories: ['tree'],
    effects: sproutEffects,
    die: sproutSDie,
    evolve: evolveToSChild('tree2'),
    maxHeight: maxSHeight,
    monthlyCarbonFixAmount: 0.272727 * 46,
  },
  {
    type: 's_tree2_child',
    name: 'クリの稚樹',
    description: '',
    categories: ['tree'],
    effects: childEffects,
    die: childSDie,
    evolve: evolveToSYoung('tree2'),
    maxHeight: maxSHeight,
    monthlyCarbonFixAmount: 0.272727 * 752,
  },
  {
    type: 's_tree2_young',
    name: 'クリの若木',
    description: '',
    categories: ['tree'],
    season: ['autumn', 'winter'],
    effects: youngEffects,
    die: youngSDie,
    evolve: evolveToSAdult('tree2'),
    maxHeight: maxSHeight,
    monthlyCarbonFixAmount: 0.272727 * 805,
  },
  {
    type: 's_tree2_adult',
    name: 'クリの成木',
    description:
      'クリ（栗）果実がいがに覆われている。縄文時代の遺跡からも出土し、古くから食糧、建材として栽培されてきた。自生種は栽培品種に比べて果実が小さい。食用とされる部分は生物学的には果実ではなく種子内部の子葉に当たる。陽樹。',
    categories: ['tree'],
    effects: adultEffects,
    die: adultSDie,
    season: ['spring', 'summer', 'winter'],
    maxHeight: maxSHeight,
    monthlyCarbonFixAmount: 0.272727 * 394,
  },
  {
    type: 's_tree3_seed',
    name: 'ヤマザクラの種',
    description: '',
    categories: ['tree-seed'],
    effects: seedEffects,
    die: seedDie,
    evolve: evolveToSSprout('tree3'),
    price: 500,
    monthlyCarbonFixAmount: 0,
  },
  {
    type: 's_tree3_sprout',
    name: 'ヤマザクラの芽',
    description: '',
    categories: ['tree'],
    effects: sproutEffects,
    die: sproutSDie,
    evolve: evolveToSChild('tree3'),
    maxHeight: maxSHeight,
    monthlyCarbonFixAmount: 0.272727 * 64,
  },
  {
    type: 's_tree3_child',
    name: 'ヤマザクラの稚樹',
    description: '',
    categories: ['tree'],
    effects: childEffects,
    die: childSDie,
    evolve: evolveToSYoung('tree3'),
    maxHeight: maxSHeight,
    monthlyCarbonFixAmount: 0.272727 * 1037,
  },
  {
    type: 's_tree3_young',
    name: 'ヤマザクラの若木',
    description: '',
    categories: ['tree'],
    season: ['autumn', 'winter'],
    effects: youngEffects,
    die: youngSDie,
    evolve: evolveToSAdult('tree3'),
    maxHeight: maxSHeight,
    monthlyCarbonFixAmount: 0.272727 * 1280,
  },
  {
    type: 's_tree3_adult',
    name: 'ヤマザクラの成木',
    description:
      'ヤマザクラ（山桜）春に山腹で淡紅白色の花を咲かせる。明治時代にソメイヨシノが育成される以前は、花見をするサクラとして古くから日本人に親しまれてきた。そのため日本各地に名所がある。陽樹。',
    categories: ['tree'],
    season: ['summer', 'autumn', 'winter'],
    effects: adultEffects,
    die: adultSDie,
    maxHeight: maxSHeight,
    monthlyCarbonFixAmount: 0.272727 * 627,
  },
];

// TODO: テンプレートのバリデーション
// Validator.raiseIfInvalid(templates);

export default templates;



// WEBPACK FOOTER //
// ./src/lib/unit_templates/unit_templates.js